import {
  type ColumnType,
  type Selectable,
  type Insertable,
  type Updateable,
} from 'kysely'
import { z } from 'zod'

import { legacySiteId, type LegacySiteId } from './LegacySite.js'
import { organizationId, type OrganizationId } from './Organization.js'
import {
  serviceCheckoutAttemptStatusId,
  type ServiceCheckoutAttemptStatusId,
} from './ServiceCheckoutAttemptStatus.js'
import { userId, type UserId } from './User.js'

/** Identifier type for public.forecast_checkout_attempt_ */
export type ForecastCheckoutAttemptId = string & {
  __brand: 'ForecastCheckoutAttemptId'
}

/** Represents the table public.forecast_checkout_attempt_ */
export default interface ForecastCheckoutAttemptTable {
  /** Database type: pg_catalog.uuid */
  id: ColumnType<
    ForecastCheckoutAttemptId,
    ForecastCheckoutAttemptId | undefined,
    ForecastCheckoutAttemptId
  >

  /** Database type: pg_catalog.timestamptz */
  createdAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  createdBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  updatedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  updatedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.timestamptz */
  deletedAt: ColumnType<Date | null, Date | string | null, Date | string | null>

  /** Database type: pg_catalog.uuid */
  deletedBy: ColumnType<UserId | null, UserId | null, UserId | null>

  /** Database type: pg_catalog.int4 */
  siteId: ColumnType<LegacySiteId, LegacySiteId, LegacySiteId>

  /** Database type: pg_catalog.int4 */
  organizationId: ColumnType<OrganizationId, OrganizationId, OrganizationId>

  /** Database type: pg_catalog.text */
  status: ColumnType<
    ServiceCheckoutAttemptStatusId,
    ServiceCheckoutAttemptStatusId | undefined,
    ServiceCheckoutAttemptStatusId
  >

  /** Database type: pg_catalog.text */
  idempotencyKey: ColumnType<string, string, string>

  /** Database type: pg_catalog.text */
  stripeRequest: ColumnType<string, string, string>

  /** Database type: pg_catalog.timestamptz */
  sessionExpiresAt: ColumnType<Date, Date | string, Date | string>

  /** Database type: pg_catalog.text */
  checkoutSessionId: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  stripeSubscriptionId: ColumnType<string | null, string | null, string | null>

  /** Database type: pg_catalog.text */
  statusReason: ColumnType<string | null, string | null, string | null>
}

export type ForecastCheckoutAttempt = Selectable<ForecastCheckoutAttemptTable>

export type ForecastCheckoutAttemptInitializer =
  Insertable<ForecastCheckoutAttemptTable>

export type ForecastCheckoutAttemptMutator =
  Updateable<ForecastCheckoutAttemptTable>

export const forecastCheckoutAttemptId =
  z.string() as unknown as z.Schema<ForecastCheckoutAttemptId>

export const forecastCheckoutAttempt = z.object({
  id: forecastCheckoutAttemptId,
  createdAt: z.coerce.date().nullable().optional(),
  createdBy: userId.nullable().optional(),
  updatedAt: z.coerce.date().nullable().optional(),
  updatedBy: userId.nullable().optional(),
  deletedAt: z.coerce.date().nullable().optional(),
  deletedBy: userId.nullable().optional(),
  siteId: legacySiteId,
  organizationId: organizationId,
  status: serviceCheckoutAttemptStatusId,
  idempotencyKey: z.string(),
  stripeRequest: z.string(),
  sessionExpiresAt: z.coerce.date(),
  checkoutSessionId: z.string().nullable().optional(),
  stripeSubscriptionId: z.string().nullable().optional(),
  statusReason: z.string().nullable().optional(),
}) as unknown as z.Schema<ForecastCheckoutAttempt>

export const forecastCheckoutAttemptInitializer = z.object({
  id: forecastCheckoutAttemptId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  siteId: legacySiteId,
  organizationId: organizationId,
  status: serviceCheckoutAttemptStatusId.optional(),
  idempotencyKey: z.string(),
  stripeRequest: z.string(),
  sessionExpiresAt: z.coerce.date(),
  checkoutSessionId: z.string().optional().nullable(),
  stripeSubscriptionId: z.string().optional().nullable(),
  statusReason: z.string().optional().nullable(),
}) as unknown as z.Schema<ForecastCheckoutAttemptInitializer>

export const forecastCheckoutAttemptMutator = z.object({
  id: forecastCheckoutAttemptId.optional(),
  createdAt: z.coerce.date().optional().nullable(),
  createdBy: userId.optional().nullable(),
  updatedAt: z.coerce.date().optional().nullable(),
  updatedBy: userId.optional().nullable(),
  deletedAt: z.coerce.date().optional().nullable(),
  deletedBy: userId.optional().nullable(),
  siteId: legacySiteId.optional(),
  organizationId: organizationId.optional(),
  status: serviceCheckoutAttemptStatusId.optional(),
  idempotencyKey: z.string().optional(),
  stripeRequest: z.string().optional(),
  sessionExpiresAt: z.coerce.date().optional(),
  checkoutSessionId: z.string().optional().nullable(),
  stripeSubscriptionId: z.string().optional().nullable(),
  statusReason: z.string().optional().nullable(),
}) as unknown as z.Schema<ForecastCheckoutAttemptMutator>
